import { isInPresentationMode } from "modules/broadcast/broadcast.firebase";
import { selectIsEventManager } from "modules/event/selectors";
import { selectCurrentTheaterId } from "modules/theater/selectors";
import { selectCurrentUserPreferences } from "modules/userPreferences/redux/selectors";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { updateGlobalUserPreference } from "modules/globalUserPreference";
import { selectIsAnonymousUser } from "modules/auth/redux/selectors";
import { EventPrechecks, PrecheckStatus } from "../constants";
import { PrecheckResult } from "../types";

export const useOnboardingCheck = (): PrecheckResult => {
  const userPreference = useSelector(selectCurrentUserPreferences);
  const isTutorialDone = userPreference?.tutorial?.done ?? false;
  const theaterId = useSelector(selectCurrentTheaterId);
  const isEventManager = useSelector(selectIsEventManager);
  const isUserAnonymous = useSelector(selectIsAnonymousUser);

  const skipOnboardingCheck =
    isEventManager || isTutorialDone || isUserAnonymous;

  const { data: isBroadcasting = false, isLoading } = useQuery({
    queryKey: ["inPresentationMode", theaterId],
    queryFn: async () => {
      if (!theaterId) {
        return undefined;
      }
      const presentationModeEnabled = await isInPresentationMode(theaterId);
      return presentationModeEnabled;
    },
    enabled: theaterId !== null && !skipOnboardingCheck,
  });

  const status = useMemo(() => {
    if (isBroadcasting || skipOnboardingCheck) {
      updateGlobalUserPreference({ tutorial: { done: true, step: 2 } });
      return PrecheckStatus.PASS;
    }

    if (isLoading || userPreference === undefined) {
      return PrecheckStatus.PENDING;
    }

    return isTutorialDone ? PrecheckStatus.PASS : PrecheckStatus.FAIL;
  }, [
    skipOnboardingCheck,
    isLoading,
    isBroadcasting,
    isTutorialDone,
    userPreference,
  ]);

  return {
    name: EventPrechecks.ONBOARDING,
    status,
  };
};
